import { Transition } from "@headlessui/react";
import { CheckCircle } from "@winamp/icons/CheckCircle.js";
import { Exclamation } from "@winamp/icons/Exclamation.js";
import { RemoveCircle } from "@winamp/icons/RemoveCircle.js";
import type { ToastMessage } from "@winamp/toast/schema.js";
import { useEffect, useState } from "react";
import type { Iconable } from "../../types";
import { classNames } from "../../utils/classNames";
import { toastIconVariants } from "./variants";

interface IToastProps extends ToastMessage {
  removeToast?: (id: string) => void;
}

const toastIconsMap: Record<ToastMessage["type"], Iconable> = {
  success: CheckCircle,
  error: RemoveCircle,
  warning: Exclamation,
};

export function Toast({ removeToast, ...toast }: IToastProps) {
  const [showToast, setShowToast] = useState(true);
  const Icon = toastIconsMap[toast.type];

  function close() {
    setShowToast(false);
    // Remove the toast from the context only after the transition ends
    setTimeout(() => {
      removeToast?.(toast.id);
    }, 300);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run when the toast.duration change
  useEffect(() => {
    const timeout = setTimeout(close, toast.duration ?? 5000);
    return () => {
      clearTimeout(timeout);
    };
  }, [toast.duration]);

  return (
    <Transition show={showToast} appear>
      <div
        role="alert"
        aria-labelledby={toast.title && `toast-title:${toast.id}`}
        aria-describedby={toast.title && `toast-description:${toast.id}`}
        className={classNames([
          "flex px-4 py-3 gap-4 bg-primary rounded-lg shadow-2xl transition ease-in-out",
          "data-[closed]:translate-x-full data-[leave]:duration-150 data-[leave]:data-[closed]:duration-300",
        ])}
      >
        <div className={toastIconVariants({ type: toast.type })}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex flex-col flex-1 justify-center">
          {toast.title && (
            <span
              id={`toast-title:${toast.id}`}
              className="text-primary text-base/6 font-bold"
            >
              {toast.title}
            </span>
          )}
          {toast.message && (
            <span
              id={`toast-description:${toast.id}`}
              className="text-secondary text-sm/5 font-normal"
            >
              {toast.message}
            </span>
          )}
        </div>
      </div>
    </Transition>
  );
}
